import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { PencilSimple, Trash, Tag } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

export default function TemplateList({ templates, triggers, onSelect, onChange, selectedId }) {
  const triggerLabel = (key) => triggers.find((t) => t.key === key)?.label || key;

  const toggle = async (t) => {
    try {
      await api.patch(`/email/templates/${t.id}`, { enabled: !t.enabled });
      toast.success(t.enabled ? "Template disabled" : "Template enabled");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  const onDelete = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await api.delete(`/email/templates/${t.id}`);
      toast.success("Template deleted");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (templates.length === 0) {
    return (
      <div className="p-10 text-center text-slate-500 text-sm" data-testid="templates-empty">
        No email templates yet. Click "New template" to create one.
      </div>
    );
  }

  return (
    <ul className="divide-y divide-slate-200" data-testid="templates-list">
      {templates.map((t) => (
        <li
          key={t.id}
          data-testid={`template-row-${t.trigger}`}
          className={cn(
            "p-5 flex items-start gap-4 transition-colors hover:bg-slate-50",
            selectedId === t.id && "bg-slate-50"
          )}
        >
          <Switch
            data-testid={`template-toggle-${t.id}`}
            checked={t.enabled}
            onCheckedChange={() => toggle(t)}
            className="mt-1"
          />
          <button
            onClick={() => onSelect(t)}
            className="flex-1 min-w-0 text-left"
          >
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-display text-lg text-slate-900">{t.name}</span>
              <span className="text-[10px] uppercase tracking-wider bg-[#002FA7]/10 text-[#002FA7] px-1.5 py-0.5 rounded-sm">{triggerLabel(t.trigger)}</span>
              {!t.enabled && (
                <span className="text-[10px] uppercase tracking-wider bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-sm">Off</span>
              )}
            </div>
            <div className="text-sm text-slate-600 mt-1 line-clamp-1">{t.subject}</div>
            {t.tags?.length > 0 && (
              <div className="flex gap-1.5 mt-2 flex-wrap">
                {t.tags.map((tag) => (
                  <span key={tag} className="inline-flex items-center gap-1 text-[11px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-sm">
                    <Tag size={10} /> {tag}
                  </span>
                ))}
              </div>
            )}
          </button>
          <div className="flex items-center gap-1 shrink-0">
            <Button
              data-testid={`edit-template-${t.id}`}
              variant="ghost"
              size="sm"
              onClick={() => onSelect(t)}
              className="rounded-sm h-8 w-8 p-0 text-slate-600 hover:text-slate-900"
            >
              <PencilSimple size={14} />
            </Button>
            <Button
              data-testid={`delete-template-${t.id}`}
              variant="ghost"
              size="sm"
              onClick={() => onDelete(t)}
              className="rounded-sm h-8 w-8 p-0 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
            >
              <Trash size={14} />
            </Button>
          </div>
        </li>
      ))}
    </ul>
  );
}
